/**
 * /decrypt API 응답 타입
 * 복호화된 사용자 정보 또는 기타 데이터를 포함합니다.
 */
interface DecryptApiResponse {
  /**
   * 복호화된 사용자 이름 (원래 authUid)
   */
  username?: string

  /**
   * 복호화된 비밀번호 (원래 authPw)
   */
  password?: string

  /**
   * 파싱된 다른 키-값 쌍들
   */
  [key: string]: string | undefined
}

// getToken 함수의 반환 타입을 명시 (Firebase 커스텀 토큰은 문자열)
interface TokenResponse {
  token: string
}

export const useCrypto = () => {
  // baseURL을 함수 스코프 최상단으로 이동
  const baseURL = import.meta.env.VITE_API_URL
  /**
   * 암호화된 데이터를 서버로 보내 복호화를 요청합니다.
   * @param encryptedData - 복호화할 암호화된 데이터 문자열
   * @returns 복호화된 데이터 (객체 또는 다른 형태)
   */
  const decryptData = async (encryptedData: string): Promise<DecryptApiResponse> => {
    if (!encryptedData) {
      console.error("[useWowpress] Encrypted data is required for decryption.")
      throw new Error("Encrypted data is required")
    }

    try {
      // baseURL 변수와 /decrypt 경로 사용
      const res = await fetch(`${baseURL}/decrypt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          encryptedData,
        }),
      })

      if (!res.ok) {
        // HTTP 에러 상태 처리 (예: 4xx, 5xx)
        const errorBody = await res.text() // 오류 메시지를 텍스트로 읽기 시도
        console.error(`[useWowpress] Failed to decrypt data. Status: ${res.status}, Body: ${errorBody}`)
        throw new Error(`Failed to decrypt data: ${res.status} ${errorBody}`)
      }

      const decryptedResult = await res.json() // 성공 시 JSON 응답 파싱
      console.log("[useWowpress] Decryption response:", decryptedResult !== null)

      return decryptedResult // 복호화된 결과 반환
    } catch (e) {
      // 네트워크 오류 또는 위에서 발생시킨 에러 처리
      console.error("[useWowpress] Failed to decrypt data:", e)
      // 에러를 다시 throw하여 호출 측에서 처리할 수 있도록 함
      throw e
    }
  }

  /**
   * 암호화된 데이터를 서버로 보내 인증을 완료하고 Firebase 커스텀 토큰을 받습니다.
   * @param encryptedData - 서버에서 복호화 및 인증에 사용할 암호화된 데이터
   * @returns Firebase 커스텀 토큰 문자열
   */
  const getToken = async (encryptedData: string): Promise<string> => {
    if (!encryptedData) {
      console.error("[useWowpress] Encrypted data is required to get token.")
      throw new Error("Encrypted data is required")
    }

    // baseURL은 함수 스코프 내에서 접근 가능해야 함 (이미 최상단에 정의되어 있음)

    try {
      // auth.ts에 정의된 /wowpress/login/encrypt 엔드포인트 사용
      const res = await fetch(`${baseURL}/auth/wowpress/login/encrypt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          encryptedData, // 요청 본문에 encryptedData 포함
        }),
      })

      if (!res.ok) {
        // HTTP 에러 상태 처리 (예: 400, 401, 500)
        const errorBody = await res.text()
        console.error(`[useWowpress] Failed to get token. Status: ${res.status}, Body: ${errorBody}`)
        // 서버에서 { message: "..." } 형식으로 에러를 보낼 수도 있으므로 파싱 시도
        try {
          const errorJson = JSON.parse(errorBody)
          throw new Error(errorJson.message || `Failed to get token: ${res.status}`)
        } catch (parseError) {
          // JSON 파싱 실패 시 원본 텍스트 사용
          throw new Error(`Failed to get token: ${res.status} ${errorBody}`)
        }
      }

      // 성공 시 JSON 응답 파싱 ({ token: "..." })
      const result: TokenResponse = await res.json()
      console.log("[useWowpress] Token received successfully.")

      if (!result.token) {
        console.error("[useWowpress] Token not found in response.")
        throw new Error("Token not found in the response from server")
      }

      return result.token // 토큰 문자열 반환
    } catch (e) {
      // 네트워크 오류 또는 위에서 발생시킨 에러 처리
      console.error("[useWowpress] Failed to get token:", e)
      // 에러를 다시 throw하여 호출 측에서 처리할 수 있도록 함
      throw e
    }
  }

  // 컴포저블이 반환하는 객체에 decryptData 함수 추가
  return {
    decryptData, // 새로 추가된 함수
    getToken, // 새로 추가된 함수
  }
}
